import { useRef } from 'react';
import { toast } from 'react-toastify';

import { useLoading } from '@/contextProviders/loadingProvider';
import {
  KnipOpdracht,
  MaakClaimOpdracht,
  VersterkInzettenOpdracht,
  VersterkOpdracht,
  VerwijderClaimOpdracht,
  VerzwakInzettenOpdracht,
  VerzwakOpdracht,
  WijzigenInzetOpdracht,
  WisselOpdracht,
} from '@/queries/mutaties/opdrachten';
import { useFetchWithAuth } from '@/queries/useFetchWithAuth';
import { toastErrorOptions, toastSuccessOptions } from '@/utils/toastOptions';

export interface Executable {
  url: string;
  body: any;
  onSuccess: string;
  onError: string;
}

export type WerklijnUpdateStatus = 'NIEUW_OF_GEWIJZIGD' | 'VERWIJDERD';

export interface WerklijnUpdate {
  id: string;
  versie: number;
  status: WerklijnUpdateStatus;
}

export type MutatieVerwerkStatus =
  'SUCCES' | 'FUNCTIONELE_FOUT_NOT_FOUND' | 'FUNCTIONELE_FOUT_CONFLICT' | 'TECHNISCHE_FOUT' | 'BMS_CONNECTIE_FOUT';

export interface MutatieResponse {
  status?: MutatieVerwerkStatus;
  wijzigingen: WerklijnUpdate[];
  errorMessage?: string | null;
}

export function useMutaties() {
  const { setLoading } = useLoading();
  const { fetchWithAuth } = useFetchWithAuth();
  // Ref-based guard to synchronously prevent double-submits. A ref is used instead of state
  // because state updates are asynchronous and may be stale inside async closures.
  const isSubmitting = useRef(false);

  const execute = async ({ url, body, onSuccess, onError }: Executable): Promise<MutatieResponse> => {
    if (isSubmitting.current) {
      return { wijzigingen: [] };
    }

    try {
      isSubmitting.current = true;
      setLoading(true);

      const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      const responseData = (await response.json()) as MutatieResponse;
      switch (responseData.status) {
        case 'SUCCES':
          toast.success(onSuccess, toastSuccessOptions);
          break;

        case 'FUNCTIONELE_FOUT_NOT_FOUND':
          if (url === '/mutaties/wissel') {
            toast.error(onError, toastErrorOptions);
            return { status: 'FUNCTIONELE_FOUT_NOT_FOUND', wijzigingen: [], errorMessage: null };
          }

          return {
            status: 'FUNCTIONELE_FOUT_NOT_FOUND',
            wijzigingen: [],
            errorMessage:
              'Mutatie is niet gelukt. (Niet gevonden). Probeer het nog een keer of neem contact op met support.',
          };

        case 'FUNCTIONELE_FOUT_CONFLICT':
          return {
            status: 'FUNCTIONELE_FOUT_CONFLICT',
            wijzigingen: [],
            errorMessage:
              'Mutatie is niet gelukt. (Conflict). Probeer het nog een keer of neem contact op met support.',
          };

        case 'TECHNISCHE_FOUT':
          return {
            status: 'TECHNISCHE_FOUT',
            wijzigingen: [],
            errorMessage:
              'Mutatie is niet gelukt vanwege een technische fout. Probeer het nog een keer of neem contact op met support.',
          };

        case 'BMS_CONNECTIE_FOUT':
          return {
            status: 'BMS_CONNECTIE_FOUT',
            wijzigingen: [],
            errorMessage: 'Er is een BMS connectiefout opgetreden. Neem contact op met support.',
          };
      }

      return responseData;
    } catch (error) {
      console.log(error);
      return {
        status: 'TECHNISCHE_FOUT',
        wijzigingen: [],
        errorMessage: 'Er is een onbekende fout opgetreden. Neem contact op met support.',
      };
    } finally {
      isSubmitting.current = false;
      setLoading(false);
    }
  };

  const knip = async (opdracht: KnipOpdracht): Promise<MutatieResponse> => {
    const onSuccess = 'Knip wordt verwerkt';
    const onError = 'Knippen is niet gelukt.';
    return execute({
      url: '/mutaties/knip',
      body: opdracht,
      onSuccess,
      onError,
    });
  };

  const makenClaim = async (opdracht: MaakClaimOpdracht): Promise<MutatieResponse> => {
    const onSuccess = 'Claim wordt verwerkt';
    const onError = 'Claim aanmaken is niet gelukt.';
    return execute({
      url: '/mutaties/makenClaim',
      body: opdracht,
      onSuccess,
      onError,
    });
  };

  const versterken = async (opdracht: VersterkOpdracht): Promise<MutatieResponse> => {
    const onSuccess = 'Versterking wordt verwerkt';
    const onError = 'Versterken is niet gelukt.';
    return execute({
      url: '/mutaties/versterk',
      body: opdracht,
      onSuccess,
      onError,
    });
  };

  const wijzigenClaim = async (opdracht: WijzigenInzetOpdracht): Promise<MutatieResponse> => {
    const onSuccess = 'Claim wordt verwerkt';
    const onError = 'Claim wijzigen is niet gelukt.';
    return execute({
      url: '/mutaties/wijzigenInzet',
      body: opdracht,
      onSuccess,
      onError,
    });
  };

  const verwijderenClaim = async (opdracht: VerwijderClaimOpdracht): Promise<MutatieResponse> => {
    const onSuccess = 'Claim wordt verwijderd';
    const onError = 'Claim verwijderen is niet gelukt.';
    return execute({
      url: '/mutaties/verwijderenClaim',
      body: opdracht,
      onSuccess,
      onError,
    });
  };

  const verzwak = async (opdracht: VerzwakOpdracht): Promise<MutatieResponse> => {
    const onSuccess = 'Verzwakking wordt verwerkt';
    const onError = 'Verzwakken is niet gelukt.';
    return execute({
      url: '/mutaties/verzwak',
      body: opdracht,
      onSuccess,
      onError,
    });
  };

  const verzwakInzetten = async (opdracht: VerzwakInzettenOpdracht): Promise<MutatieResponse> => {
    const onSuccess = 'Verzwakking van inzetten wordt verwerkt';
    const onError = 'Verzwakken van inzetten is niet gelukt.';
    return execute({
      url: '/mutaties/verzwakInzetten',
      body: opdracht,
      onSuccess,
      onError,
    });
  };

  const versterkInzetten = async (opdracht: VersterkInzettenOpdracht): Promise<MutatieResponse> => {
    const onSuccess = 'Versterking van inzetten wordt verwerkt';
    const onError = 'Versterking van inzetten is niet gelukt.';
    return execute({
      url: '/mutaties/versterkWerklijndeel',
      body: opdracht,
      onSuccess,
      onError,
    });
  };

  const wijzigenInzet = async (opdracht: WijzigenInzetOpdracht): Promise<MutatieResponse> => {
    const onSuccess = 'Inzet wordt gewijzigd';
    const onError = 'Inzet wijzigen is niet gelukt.';
    return execute({
      url: '/mutaties/wijzigenInzet',
      body: opdracht,
      onSuccess,
      onError,
    });
  };

  const wissel = async (opdracht: WisselOpdracht): Promise<MutatieResponse> => {
    const onSuccess = 'Wissel wordt verwerkt';
    const onError = 'Wissel is niet gelukt.';
    return execute({
      url: '/mutaties/wissel',
      body: opdracht,
      onSuccess,
      onError,
    });
  };

  return {
    knip,
    makenClaim,
    verwijderenClaim,
    wijzigenClaim,
    versterken,
    verzwak,
    verzwakInzetten,
    versterkInzetten,
    wijzigenInzet,
    wissel,
  };
}
